import React, { useState } from "react";
import { useQuery } from "react-query";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import DataTable from "../../../components/shared/DataTable";
import { formatDate, formatCurrency } from "../../../utils/helpers";
import api from "../../../utils/api";

const REPORT_TYPES = [
  { key: "doctor_booking", label: "Doctor-wise Bookings" },
  { key: "lab_booking", label: "Lab-wise Bookings" },
];

const PERIODS = [
  { key: "day", label: "Today" },
  { key: "week", label: "This Week" },
  { key: "month", label: "This Month" },
  { key: "custom", label: "Custom Range" },
];

export default function Reports() {
  const [reportType, setReportType] = useState("doctor_booking");
  const [period, setPeriod] = useState("month");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const { data, isLoading, refetch } = useQuery(
    ["admin-reports", reportType, period, dateFrom, dateTo],
    () =>
      api.get(
        `/admin/reports?type=${reportType}&period=${period}&from=${dateFrom}&to=${dateTo}`
      ),
    { enabled: true }
  );

  const handleExport = async () => {
    const response = await api.get(
      `/admin/reports/export?type=${reportType}&period=${period}&from=${dateFrom}&to=${dateTo}`,
      { responseType: "blob" }
    );
    const url = window.URL.createObjectURL(new Blob([response]));
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `report_${reportType}_${period}.xlsx`);
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  const tableColumns =
    reportType === "doctor_booking"
      ? [
          { key: "doctorName", label: "Doctor Name", sortable: true },
          { key: "specialization", label: "Specialization" },
          { key: "totalBookings", label: "Total Bookings", sortable: true },
          {
            key: "revenue",
            label: "Revenue",
            render: (row) => formatCurrency(row.revenue),
            sortable: true,
          },
        ]
      : [
          { key: "labName", label: "Lab Name", sortable: true },
          { key: "totalBookings", label: "Total Bookings", sortable: true },
          {
            key: "revenue",
            label: "Revenue",
            render: (row) => formatCurrency(row.revenue),
            sortable: true,
          },
        ];

  return (
    <div className="space-y-5">
      {/* Filter Bar */}
      <div className="card flex flex-wrap gap-4 items-end">
        <div>
          <label className="block text-xs font-semibold text-slate-500 mb-1 uppercase tracking-wide">
            Report Type
          </label>
          <select
            value={reportType}
            onChange={(e) => setReportType(e.target.value)}
            className="input-field w-auto"
          >
            {REPORT_TYPES.map((r) => (
              <option key={r.key} value={r.key}>
                {r.label}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs font-semibold text-slate-500 mb-1 uppercase tracking-wide">
            Period
          </label>
          <select
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
            className="input-field w-auto"
          >
            {PERIODS.map((p) => (
              <option key={p.key} value={p.key}>
                {p.label}
              </option>
            ))}
          </select>
        </div>
        {period === "custom" && (
          <>
            <div>
              <label className="block text-xs font-semibold text-slate-500 mb-1 uppercase tracking-wide">
                From
              </label>
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="input-field"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-500 mb-1 uppercase tracking-wide">
                To
              </label>
              <input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="input-field"
              />
            </div>
          </>
        )}
        <button onClick={() => refetch()} className="btn-primary">
          Apply
        </button>
        <button onClick={handleExport} className="btn-secondary">
          📥 Export Excel
        </button>
        <button onClick={() => window.print()} className="btn-secondary">
          🖨️ Print
        </button>
      </div>

      {/* Chart */}
      <div className="card">
        <h3 className="font-display font-semibold text-slate-800 mb-4">
          Booking Trend
        </h3>
        <ResponsiveContainer width="100%" height={260}>
          <BarChart data={data?.chartData || []}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
            <XAxis dataKey="label" tick={{ fontSize: 12 }} />
            <YAxis tick={{ fontSize: 12 }} />
            <Tooltip />
            <Legend />
            <Bar dataKey="bookings" fill="#0ea5e9" radius={[4, 4, 0, 0]} name="Bookings" />
            <Bar dataKey="revenue" fill="#14b8a6" radius={[4, 4, 0, 0]} name="Revenue (₹)" />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Table */}
      <DataTable
        columns={tableColumns}
        data={data?.rows || []}
        loading={isLoading}
        emptyMessage="No report data available."
      />
    </div>
  );
}
